import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react'
import { useBackgroundScrollLock } from './useBackgroundScrollLock'
import { registerOpenSheet } from '../utils/sheetOpenState'

interface UseSheetDragDismissOptions {
  /** 임계값(px)을 넘겨 아래로 끌면 onClose 호출 */
  onClose: () => void
  /** 닫힘 판정 임계값(px). 기본 100 */
  threshold?: number
  /** 비활성화(예: 시트가 닫혀있을 때) */
  enabled?: boolean
}

/**
 * 바텀시트 핸들바 "아래로 끌어서 닫기"(drag-to-dismiss) 공용 훅.
 * - handleProps: 핸들 영역(그랩 바 wrapper)에 spread (터치 + 마우스)
 * - sheetStyle: 시트 패널 div에 적용 → 드래그하는 만큼 아래로 따라 내려감
 * 임계값을 넘기면 onClose, 아니면 원위치로 스냅.
 */
export function useSheetDragDismiss({
  onClose,
  threshold = 100,
  enabled = true,
}: UseSheetDragDismissOptions) {
  const [dragY, setDragY] = useState(0)
  const [dragging, setDragging] = useState(false)
  const startYRef = useRef(0)
  const draggingRef = useRef(false)

  // 시트가 열린 동안 배경 스크롤 잠금 (시트 내부 스크롤 영역은 허용)
  useBackgroundScrollLock(enabled)

  // 시트가 열린 동안 전역 등록 → 스와이프 뒤로가기(useSwipeBack)가 발동하지 않게 한다.
  useEffect(() => {
    if (!enabled) return
    const release = registerOpenSheet()
    return release
  }, [enabled])

  const onStart = useCallback(
    (clientY: number) => {
      if (!enabled) return
      draggingRef.current = true
      setDragging(true)
      startYRef.current = clientY
    },
    [enabled]
  )

  const onMove = useCallback((clientY: number) => {
    if (!draggingRef.current) return
    const delta = clientY - startYRef.current
    setDragY(delta > 0 ? delta : 0) // 아래로만 따라감
  }, [])

  const onEnd = useCallback(() => {
    if (!draggingRef.current) return
    draggingRef.current = false
    setDragging(false)
    setDragY((cur) => {
      if (cur > threshold) {
        onClose()
      }
      return 0
    })
  }, [onClose, threshold])

  // 드래그 중에만: 마우스 전역 리스너 (배경 스크롤 차단은 useBackgroundScrollLock이 담당)
  useEffect(() => {
    if (!dragging) return
    const mm = (e: MouseEvent) => onMove(e.clientY)
    const mu = () => onEnd()
    window.addEventListener('mousemove', mm)
    window.addEventListener('mouseup', mu)
    return () => {
      window.removeEventListener('mousemove', mm)
      window.removeEventListener('mouseup', mu)
    }
  }, [dragging, onMove, onEnd])

  const handleProps = {
    onTouchStart: (e: React.TouchEvent) => onStart(e.touches[0].clientY),
    onTouchMove: (e: React.TouchEvent) => onMove(e.touches[0].clientY),
    onTouchEnd: () => onEnd(),
    onMouseDown: (e: React.MouseEvent) => {
      e.preventDefault()
      onStart(e.clientY)
    },
    // 핸들 영역에서 드래그 중 스크롤/제스처 방지
    style: { touchAction: 'none' as const, cursor: 'grab' },
  }

  const sheetStyle: CSSProperties = {
    transform: dragY ? `translateY(${dragY}px)` : undefined,
    transition: dragging ? 'none' : 'transform 0.25s ease',
  }

  return { handleProps, sheetStyle, dragY, dragging }
}
